/**
 * Umumiy reaksiya moduli — tiplar. CSTR va PFR modellari birga ishlatadi.
 *
 * Reaksiya: a·A → b·B (bitta, qaytmas). Kinetika A ning sarflanish tezligi
 * orqali beriladi (Levenspiel belgilashi):
 *   −r_A = k(T)·C_A^n      [mol A/(m³·s)]
 *   k(T) = k0·exp(−Ea/(R·T))  (Arrhenius)
 * Stexiometriya faqat B ning hosil bo'lishiga ta'sir qiladi: C_B = C_B0 + (b/a)·(C_A0 − C_A).
 * Reaksiya issiqligi ΔH_r — 1 mol A sarflanishiga (J/mol A), manfiy = ekzotermik.
 */

export interface ReactionFluid {
  /** Aralashma zichligi, kg/m³ (o'zgarmas deb qabul qilinadi) */
  density: number;
  /** Aralashma issiqlik sig'imi, J/(kg·K) */
  cp: number;
}

export interface ReactionParams {
  /**
   * Oldeksponensial ko'paytuvchi k0. Birligi tartibga bog'liq:
   * (mol/m³)^(1−n)/s. n = 1 uchun 1/s.
   */
  k0: number;
  /** Aktivlanish energiyasi, J/mol */
  Ea: number;
  /** Reaksiya tartibi n > 0 (tipik 0.5, 1, 1.5, 2). Standart 1. */
  order?: number;
  /** Stexiometrik koeffitsientlar a·A → b·B. Standart {a: 1, b: 1}. */
  stoichiometry?: { a: number; b: number };
  /** Reaksiya issiqligi, J/mol A (manfiy = ekzotermik). Standart 0. */
  dHr?: number;
  /**
   * Aralashma xossalari to'g'ridan-to'g'ri (ρ, cp). Berilmasa `solvent` kerak.
   * Tipik suvli eritma: {density: 1000, cp: 4180} (Kasatkin, ilova jadvallari).
   */
  fluid?: ReactionFluid;
  /**
   * Erituvchi identifikatori (`properties`: 'water', 'ethanol', ...): ρ va cp
   * suyuq fazada kirish haroratida hisoblanadi (ASSUMPTION: aralashma xossalari
   * toza erituvchinikiga teng).
   */
  solvent?: string;
}

/**
 * Reaksiya kutubxonasiga havola (packs/<id>/reactions.json): kinetika, stexiometriya va
 * ΔH_r kutubxonadan olinadi; qolgan maydonlar — o'quv variant uchun ustiga yozish (ixtiyoriy).
 * Faqat qaytmas (equilibrium'siz) reaksiyalar: a·A → b·B ko'rinishiga keltiriladi
 * (A — kalit reagent, B — birinchi mahsulot).
 */
export interface LibraryReactionRef {
  library: string;
  /** katalizator yoshi, s — faollik pasayishi k0 ga ko'paytiriladi */
  catalystAge?: number;
  fluid?: ReactionFluid;
  solvent?: string;
  k0?: number;
  Ea?: number;
  order?: number;
  dHr?: number;
  stoichiometry?: { a: number; b: number };
}

export type ReactionSpec = ReactionParams | LibraryReactionRef;

export function isLibraryRef(r: ReactionSpec): r is LibraryReactionRef {
  return typeof (r as LibraryReactionRef).library === 'string';
}

export type ThermalMode = 'isothermal' | 'adiabatic' | 'jacketed';

/** Reaksiya moduli hisoblagan doimiy xossalar (bir hisob uchun). */
export interface ReactionMixture {
  density: number;
  cp: number;
  /** ρ·cp, J/(m³·K) */
  rhoCp: number;
  order: number;
  /** b/a — 1 mol A ga hosil bo'ladigan B mollari */
  yieldB: number;
  /** −ΔH_r, J/mol A (musbat = ekzotermik) */
  heatRelease: number;
}
